/**
 * Shared pieces for the Billing & Analytics page.
 *
 * These exist as primitives rather than inline markup because the page has to
 * say the same things in several places — a figure with a help affordance, a
 * change indicator, a proportion bar — and an admin reading a number in a tile
 * and the same number in a table must not have to work out whether they mean
 * the same thing.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'

/** True when the viewer has asked the OS for less motion. */
export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  )
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!mq) return
    const onChange = e => setReduced(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

/** Standard page card. One definition so every block shares an edge and radius. */
export function Card({ className = '', children, ...rest }) {
  return (
    <div
      className={`bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 ${className}`}
      {...rest}
    >
      {children}
    </div>
  )
}

export function SectionTitle({ icon: Icon, children, help, right }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{children}</h2>
      {help && <HelpTip>{help}</HelpTip>}
      {right && <div className="ml-auto">{right}</div>}
    </div>
  )
}

/**
 * Hold the page still while an overlay is open.
 *
 * Without this the page behind a modal scrolls under it — you close the dialog
 * and find yourself somewhere else — and on a short viewport the modal itself
 * ends up scrolling inside a scrolling page, which is what produced the blank
 * run of space above and below the plan comparison.
 */
export function useBodyScrollLock(active) {
  useEffect(() => {
    if (!active) return
    const { overflow, paddingRight } = document.body.style
    // Replace the scrollbar's width with padding, or the page jumps sideways
    // the moment it is hidden.
    const gap = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (gap > 0) document.body.style.paddingRight = `${gap}px`
    return () => {
      document.body.style.overflow = overflow
      document.body.style.paddingRight = paddingRight
    }
  }, [active])
}

/**
 * The "?" affordance.
 *
 * Every figure on this page is derived from something — pages come out of job
 * results, costs out of published Azure rates — and an admin who cannot see
 * where a number came from does not trust it. Opens on hover and on focus, so
 * it is reachable without a mouse.
 *
 * The bubble renders in a portal, positioned against the trigger's rectangle
 * in viewport coordinates. Kept inline it was clipped by the first ancestor
 * with `overflow: hidden` — which is every card on this page — and ran off the
 * screen edge on the right-hand column.
 */
export function HelpTip({ children, label = 'What is this?' }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)
  const triggerRef = useRef(null)
  const bubbleRef = useRef(null)
  const id = useId()

  const place = useCallback(() => {
    const trigger = triggerRef.current
    const bubble = bubbleRef.current
    if (!trigger || !bubble) return

    const anchor = trigger.getBoundingClientRect()
    const { width, height } = bubble.getBoundingClientRect()
    const margin = 8

    // Centre on the trigger, then pull back inside whichever edge it crosses.
    let left = anchor.left + anchor.width / 2 - width / 2
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin))

    // Below by default; above when there is no room, which is what happens to
    // the figures sitting at the bottom of the plan panel.
    const below = anchor.bottom + margin
    const above = anchor.top - height - margin
    const top = below + height + margin > window.innerHeight && above > margin ? above : below

    setPos({ left, top })
  }, [])

  useLayoutEffect(() => {
    if (!open) { setPos(null); return }
    place()
  }, [open, place])

  useEffect(() => {
    if (!open) return
    // Anything that moves the trigger invalidates the position, and a bubble
    // left behind pointing at nothing is worse than no bubble.
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return (
    <span className="relative inline-flex items-center align-middle">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-describedby={open ? id : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={e => { e.stopPropagation(); setOpen(v => !v) }}
        className="w-4 h-4 rounded-full border border-gray-300 dark:border-gray-600 text-[9px] font-bold
                   text-gray-400 dark:text-gray-500 leading-none grid place-items-center
                   hover:bg-navy-600 hover:border-navy-600 hover:text-white
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 transition-colors"
      >
        ?
      </button>
      {open && createPortal(
        <span
          ref={bubbleRef}
          id={id}
          role="tooltip"
          style={{
            position: 'fixed',
            left: pos ? `${pos.left}px` : 0,
            top: pos ? `${pos.top}px` : 0,
            // Measured before it is shown, so the first frame is not a flash
            // of an unplaced bubble in the corner.
            visibility: pos ? 'visible' : 'hidden',
          }}
          className="z-[70] w-60 max-w-[calc(100vw-1rem)] rounded-lg bg-gray-900 dark:bg-gray-800
                     text-gray-100 text-[11.5px] leading-relaxed px-3 py-2 shadow-xl
                     ring-1 ring-black/10 dark:ring-white/10 animate-panel-in
                     font-normal normal-case tracking-normal pointer-events-none"
        >
          {children}
        </span>,
        document.body,
      )}
    </span>
  )
}

/**
 * Number that counts up on mount and on change.
 *
 * Purely an attention device: the eye follows the movement to the figure that
 * changed, which is what makes a range switch feel like the same number moving
 * rather than the page replacing itself.
 */
export function CountUp({ value, format = v => Math.round(v).toLocaleString(), duration = 700, className = '' }) {
  const reduced = usePrefersReducedMotion()
  const [display, setDisplay] = useState(reduced ? value : 0)
  const fromRef = useRef(0)
  const frameRef = useRef(null)

  useEffect(() => {
    if (reduced) { setDisplay(value); return }
    const from = fromRef.current
    const to = Number(value) || 0
    if (from === to) { setDisplay(to); return }
    const start = performance.now()

    const tick = now => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)          // easeOutCubic
      setDisplay(from + (to - from) * eased)
      if (t < 1) frameRef.current = requestAnimationFrame(tick)
      else fromRef.current = to
    }
    frameRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameRef.current)
  }, [value, duration, reduced])

  useEffect(() => { if (reduced) fromRef.current = Number(value) || 0 }, [value, reduced])

  return <span className={`tabular-nums ${className}`}>{format(display)}</span>
}

/**
 * Period-on-period change.
 *
 * `inverse` flips the colouring for metrics where up is bad — spend rises are
 * not good news, but request volume rising usually is. `isNew` is a previous
 * billing month that had none of this: there was a prior period, but no
 * percentage of zero.
 */
export function Delta({ value, inverse = false, isNew = false, suffix = 'vs same point last billing month' }) {
  if (isNew) {
    return (
      <span
        className="text-[11px] text-gray-500 dark:text-gray-400"
        title="None at the same point of the previous billing month"
      >
        New this billing month
      </span>
    )
  }
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return <span className="text-[11px] text-gray-400 dark:text-gray-500">No prior period</span>
  }
  const rounded = Math.round(value)
  const flat = Math.abs(rounded) < 1
  const up = rounded > 0
  const good = flat ? false : (inverse ? !up : up)
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight
  const tone = flat
    ? 'text-gray-400 dark:text-gray-500'
    : good
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-amber-600 dark:text-amber-400'

  return (
    <span
      className="inline-flex items-center gap-1 text-[11px]"
      title="Compared with the same point of the previous billing month, so a part-finished billing month is not measured against a full one"
    >
      <span className={`inline-flex items-center gap-0.5 font-semibold tabular-nums ${tone}`}>
        <Icon className="w-3 h-3" />
        {flat ? 'Flat' : `${Math.abs(rounded)}%`}
      </span>
      <span className="text-gray-400 dark:text-gray-500">{suffix}</span>
    </span>
  )
}

/**
 * Sparkline for a KPI tile — shape only, no axes.
 *
 * It answers "is this rising or falling" at a glance; the tile's own figure and
 * the chart below carry the exact values.
 */
export function Sparkline({ data, color, height = 28 }) {
  const values = (data || []).map(v => Number(v) || 0)
  if (values.length < 2) return <div style={{ height }} />

  const max = Math.max(...values)
  const min = Math.min(...values)
  const span = max - min || 1
  const width = 100
  const step = width / (values.length - 1)
  const points = values.map((v, i) => [i * step, height - ((v - min) / span) * (height - 4) - 2])
  const line = points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
  const area = `${line} ${width},${height} 0,${height}`
  const gradientId = `spark-${color.replace('#', '')}`

  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full" style={{ height }} aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#${gradientId})`} />
      <polyline
        points={line}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2.5" fill={color} />
    </svg>
  )
}

/** Segmented control — ranges, period toggles. */
export function Segmented({ options, value, onChange, size = 'sm', ariaLabel }) {
  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-[13px]'
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex items-center gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
      {options.map(opt => {
        const active = value === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(opt.value)}
            className={`${pad} font-medium rounded-md transition-all duration-200 whitespace-nowrap
              focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500
              ${active
                ? 'bg-white dark:bg-gray-700 text-navy-700 dark:text-navy-300 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/** Tab bar with a sliding underline. */
export function Tabs({ tabs, value, onChange }) {
  const listRef = useRef(null)

  const onKeyDown = e => {
    const idx = tabs.findIndex(t => t.value === value)
    if (e.key === 'ArrowRight') { e.preventDefault(); onChange(tabs[(idx + 1) % tabs.length].value) }
    if (e.key === 'ArrowLeft') { e.preventDefault(); onChange(tabs[(idx - 1 + tabs.length) % tabs.length].value) }
  }

  return (
    <div className="border-b border-gray-200 dark:border-gray-800">
      <div ref={listRef} role="tablist" onKeyDown={onKeyDown} className="flex gap-1 overflow-x-auto no-scrollbar -mb-px">
        {tabs.map(tab => {
          const active = tab.value === value
          const Icon = tab.icon
          return (
            <button
              key={tab.value}
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(tab.value)}
              className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium whitespace-nowrap
                transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded-t-md
                ${active
                  ? 'text-navy-700 dark:text-navy-300'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
            >
              {Icon && <Icon className="w-4 h-4" />}
              {tab.label}
              <span
                className={`absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-navy-600 dark:bg-navy-400
                            origin-left transition-transform duration-300 ease-out
                            ${active ? 'scale-x-100' : 'scale-x-0'}`}
              />
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Proportion bar used inside tables — magnitude without a second chart. */
export function ShareBar({ value, max, color = 'bg-navy-600 dark:bg-navy-400', label }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div
          className={`h-full rounded-full ${color} transition-[width] duration-700 ease-out`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {label && <span className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400 w-10 text-right">{label}</span>}
    </div>
  )
}

/** Empty state shared by every panel, so "no data" always looks the same. */
export function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="text-center py-10 px-4">
      {Icon && <Icon className="w-8 h-8 mx-auto mb-3 text-gray-300 dark:text-gray-600" />}
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</p>
      {body && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">{body}</p>}
    </div>
  )
}
